import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import TableComponent from "../../CommonComponents/Table";
import {
  vendorLocationSearchByOptions,
  vendorServicibleLocationsHealthColumnData,
  vendorServicibleLocationsMotorColumnData,
} from "../../constants/utils";
import {
  getServiceableLocations,
  editVendorServiceLocation,
  removeVendorServiceLocation,
  getSubProductsByLob,
} from "../../Services/vendorManagement.service";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { capitalize } from "lodash";
import { secureData } from "../../Encryption/encryption";
import CustomSelect from "../../CommonComponents/Select/CustomSelect";
import SearchIcon from '@mui/icons-material/Search';
import useToaster from "../../customHook/useToaster";
import TableSkeleton from "../../CommonComponents/TableSkeleton";
import AddLocationModal from "./AddLocationModal";
import styles from "./VendorManagement.module.css";
import CommonInput from "../../CommonComponents/CommonInput";

const getLabelsFromIds = (ids = [], apiSubProducts = []) => {
  if (!Array.isArray(ids)) return [];
  const labels = [];
  ids.forEach((id) => {
    const match = apiSubProducts.find((sp) => sp.sub_product_id === id);
    if (match) labels.push(match.sub_product_name);
  });
  return labels;
};

const getIdsFromLabels = (labels = [], apiSubProducts = []) => {
  if (!Array.isArray(labels)) return [];
  const ids = [];
  labels.forEach((label) => {
    const match = apiSubProducts.find((sp) => sp.sub_product_name === label);
    if (match) ids.push(match.sub_product_id);
  });
  return ids;
};

const SubProductEditor = ({
  initialLabels,
  onChangeRef,
  availableOptions = [],
}) => {
  const [localVal, setLocalVal] = useState(initialLabels);

  const handleChange = (e) => {
    const val = e.target.value;
    const newArr = typeof val === "string" ? val.split(",") : val;
    setLocalVal(newArr);
    onChangeRef(newArr);
  };

  return (
    <CustomSelect
      options={availableOptions}
      customSelectValue={localVal}
      handleChange={handleChange}
      placeholder="Select Product"
      height="3rem"
      multiple={true}
      boxStyles={{ width: "140px" }}
    />
  );
};

const CapacityEditor = ({ initialValue, onChangeRef }) => {
  const [localVal, setLocalVal] = useState(initialValue);

  const handleChange = (e) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, "");
    setLocalVal(numericValue);
    onChangeRef(numericValue);
  };

  return (
    <input
      type="text"
      value={localVal}
      placeholder="Capacity"
      className={styles["capacityInput"]}
      onChange={handleChange}
    />
  );
};

const ServiceableLocations = ({ vendorId, vendorDetails }) => {
  const [openCollapse, setOpenCollapse] = useState();
  const [employeeId, setEmployeeId] = useState(null);
  const [isLoading, setIsloading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const loginToken = useSelector((state) => state?.loginSlice?.userData?.token);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [apiLocations, setApiLocations] = useState([]);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedSearchByOption, setSelectedSearchByOption] = useState("");
  const editFormDataRef = useRef({ sub_products: [], capacity_per_week: "" });
  const { showToast } = useToaster();
  const [fetchedHealthSubProducts, setFetchedHealthSubProducts] = useState([]);
  const isMotorOnly =
    String(vendorDetails?.is_motor) === "1" &&
    String(vendorDetails?.is_health) === "0";

  useEffect(() => {
    if (!isMotorOnly) {
      const fetchHealthSubProducts = async () => {
        try {
          const res = await getSubProductsByLob(2);
          if (res?.success) {
            setFetchedHealthSubProducts(res.data || []);
          }
        } catch (error) {
          console.error("Failed to fetch health sub products:", error);
        }
      };
      fetchHealthSubProducts();
    }
  }, [isMotorOnly]);

  const fetchServiceableLocations = async (empId) => {
    if (!vendorId || !empId) return;
    setIsloading(true);
    try {
      const payload = {
        vendor_company_id: vendorId,
        page: page,
        pageSize: rowsPerPage,
      };
      const response = await getServiceableLocations(payload);
      if (response?.success) {
        setApiLocations(response?.data?.locations || []);
        setTotalCount(response?.data?.totalRecords);
      }
    } catch (error) {
      showToast(error?.message, "error", true);
      console.error("Failed to fetch locations:", error);
    } finally {
      setIsloading(false);
    }
  };

  const handleEditClick = (index, row) => {
    editFormDataRef.current = {
      sub_products: getLabelsFromIds(
        row?.sub_product_ids,
        fetchedHealthSubProducts
      ),
      capacity_per_week: row?.capacity_per_week || "",
    };
    setEditingRowIndex(index);
  };

  const handleCancelClick = () => {
    setEditingRowIndex(null);
  };

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleInputChange = (e) => {
    if (!selectedSearchByOption) {
      showToast("Kindly select the source to search for loactions", "warning", true)
      return;
    }
    setSearchValue(e.target.value)
  }

  const handleSaveClick = async (row) => {
    const currentFormData = editFormDataRef.current;
    const locationObj = {
      service_location_id: row?.service_location_id,
      state: row?.serviced_state,
      city: row?.serviced_city,
    };
    if (!isMotorOnly) {
      locationObj.sub_product_ids = getIdsFromLabels(
        currentFormData.sub_products,
        fetchedHealthSubProducts
      );
      locationObj.capacity_per_week = Number(currentFormData.capacity_per_week);
    }
    const payload = {
      vendor_company_id: vendorId,
      locations: [locationObj],
    };
    try {
      const response = await editVendorServiceLocation(payload);
      if (response?.success) {
        showToast("Service location updated successfully!", "success", true);
        setEditingRowIndex(null);
        fetchServiceableLocations(employeeId);
      }
    } catch (error) {
      console.error(error);
      showToast("Error updating service location.", "error", true);
    }
  };

  const handleDeleteClick = async (row) => {
    const payload = {
      vendor_company_id: vendorId,
      locations: [row?.service_location_id],
    };
    try {
      const response = await removeVendorServiceLocation(payload);
      if (response?.success) {
        showToast("Service location deleted successfully!", "success", true);
        fetchServiceableLocations(employeeId);
      }
    } catch (error) {
      showToast("Error deleting service location.", "error", true);
      console.error(error);
    }
  };

  const handleSearchClick = async (searchKey) => {
    if (!vendorId) return;
    setIsloading(true);
    try {
      const payload = {
        vendor_company_id: vendorId,
        page: page,
        pageSize: rowsPerPage,
        [selectedSearchByOption.toLowerCase()]: searchKey.trim(),
      };
      const response = await getServiceableLocations(payload);
      if (response?.success) {
        showToast(response?.message, "success", true);
        setApiLocations(response?.data?.locations || []);
        setTotalCount(response?.data?.totalRecords);
      }
    } catch (error) {
      showToast(error?.message, "error", true);
      console.error("Failed to fetch locations:", error);
    } finally {
      setIsloading(false);
    }
  };

  const tableData = useMemo(() => {
    return apiLocations.map((row, index) => {
      const isEditing = editingRowIndex === index;

      const rowData = {
        ...row,
        serviced_city: capitalize(row.serviced_city) || "N/A",
        serviced_state: capitalize(row.serviced_state) || "N/A",
        "sr_no.": index + 1,

        action: isEditing ? (
          <div className={styles["actionGroup"]}>
            <span
              className={styles["saveActionText"]}
              onClick={(e) => {
                e.stopPropagation();
                handleSaveClick(row);
              }}
            >
              Save
            </span>
            <span
              className={styles["cancelActionText"]}
              onClick={(e) => {
                e.stopPropagation();
                handleCancelClick();
              }}
            >
              Cancel
            </span>
          </div>
        ) : (
          <div className={styles["actionGroup"]}>
            {!isMotorOnly && (
              <span
                className={styles["editActionText"]}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(index, row);
                }}
              >
                Edit
              </span>
            )}
            <span
              className={styles["deleteActionText"]}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
            >
              Delete
            </span>
          </div>
        ),
      };

      if (!isMotorOnly) {
        const subProducts = row?.sub_products?.map(
          (sub) => " " + capitalize(sub.name)
        );
        rowData.sub_product_ids = isEditing ? (
          <SubProductEditor
            initialLabels={getLabelsFromIds(
              row.sub_product_ids,
              fetchedHealthSubProducts
            )}
            availableOptions={fetchedHealthSubProducts.map(
              (sp) => sp.sub_product_name
            )}
            onChangeRef={(newVal) => {
              editFormDataRef.current.sub_products = newVal;
            }}
          />
        ) : subProducts ? (
          subProducts.sort().toString()
        ) : (
          "N/A"
        );

        rowData.capacity_per_week = isEditing ? (
          <CapacityEditor
            initialValue={row.capacity_per_week || ""}
            onChangeRef={(newVal) => {
              editFormDataRef.current.capacity_per_week = newVal;
            }}
          />
        ) : (
          row.capacity_per_week || "N/A"
        );
      }

      return rowData;
    });
  }, [apiLocations, editingRowIndex, isMotorOnly, fetchedHealthSubProducts]);

  const firstButtonClicked = useCallback(() => {
    setPage(1);
  }, []);
  const lastButtonClicked = useCallback(() => {
    setPage(Math.ceil(totalCount / rowsPerPage));
  }, [rowsPerPage, totalCount]);
  const handleNextButtonClick = useCallback(() => {
    setPage((page) => page + 1);
  }, []);
  const handleBackButtonClick = useCallback(() => {
    setPage((page) => page - 1);
  }, []);
  const onPageDropdownChange = useCallback((e) => {
    setPage(Number(e.target.value));
  }, []);

  useEffect(() => {
    if (!loginToken) return;
    try {
      const decodedToken = secureData(loginToken, "string_decrypt");
      const infoFromToken = jwtDecode(decodedToken);
      const decodedEmployeeId = infoFromToken?.employeeId;
      setEmployeeId(decodedEmployeeId);
      fetchServiceableLocations(decodedEmployeeId);
    } catch (e) {
      showToast("Error in retrieving user data please try again!");
    }
  }, [loginToken, page, rowsPerPage]);

  return (
    <div>
      <div className={styles["addLocationHeader"]}>
        <button onClick={handleAddClick} className={styles["addLocationBtn"]}>
          + Add Location
        </button>
        <div className={styles["searchContainer"]}>
          <div className={styles["searchSelectWrapper"]}>
            <CustomSelect
              showLabel={false}
              placeholder="Search By"
              options={vendorLocationSearchByOptions}
              customSelectValue={selectedSearchByOption}
              handleChange={(e) => setSelectedSearchByOption(e.target.value)}
            />
          </div>
          <div className={styles["searchInputWrapper"]}>
            <CommonInput
              name="search"
              value={searchValue}
              placeholder="Search locations..."
              onChange={handleInputChange}
              removeBackGround
            />
          </div>
          <button 
            className={styles["searchBtn"]} 
            onClick={() => handleSearchClick(searchValue)}
          >
            <SearchIcon fontSize="large"/>
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <TableSkeleton />
      ) : apiLocations.length === 0 ? (
        <div className={styles["noLocationsText"]}>
          No locations assigned to the vendor.
        </div>
      ) : (
        <TableComponent
          rowsData={tableData}
          columnsData={
            !isMotorOnly
              ? vendorServicibleLocationsHealthColumnData
              : vendorServicibleLocationsMotorColumnData
          }
          stickyHeader={false}
          open={openCollapse}
          setOpen={setOpenCollapse}
          TableCollapseContentDocument={false}
          paddingLeftHeaderStyledTableCell="-15px"
          paddingHeaderStyledTableCell="10.5px"
          paddingBodyStyledTableCell="15.5px"
          paddingLeftBodyStyledTableCell="0px"
          columnHeaderColor={"#666666"}
          columnHeaderFont="1.1rem"
          columnHeaderFontWeight={400}
          columnBodyFont="1.2rem"
          columnBodyFontWeight={500}
          paddingHeader=".75rem"
          page={page}
          rowsPerPage={rowsPerPage}
          count={totalCount}
          firstButtonClicked={firstButtonClicked}
          lastButtonClicked={lastButtonClicked}
          handleNextButtonClick={handleNextButtonClick}
          handleBackButtonClick={handleBackButtonClick}
          onPageDropdownChange={onPageDropdownChange}
          setPage={setPage}
        />
      )}
      <AddLocationModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        vendorId={vendorId}
        employeeId={employeeId}
        isMotorOnly={isMotorOnly}
        onSuccess={() => fetchServiceableLocations(employeeId)}
      />
    </div>
  );
};

export default ServiceableLocations;